import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from 'utils';
import { RootState } from 'data/root.reducers';
import * as api from './natConfiguration.api';
import { getSelectedNatConfiguration } from './natConfiguration.selectors';
import { SYSTEM_CONFIGURATION_ROUTE, NAT_CONFIGURATION_ROUTE, NETWORK } from '../../constants';
import {
  GET_NAT_CONFIGURATION_LIST,
  GET_NAT_CONFIGURATION,
  CREATE_NAT_CONFIGURATION,
  DELETE_NAT_CONFIGURATION,
  GO_TO_NAT_CONFIGURATION_LIST,
  GO_TO_NAT_CONFIGURATION_VIEW,
  GO_TO_NAT_CONFIGURATION_CREATE,
  SELECT_NAT_CONFIGURATION,
  SET_NAT_CONFIGURATION_LOADING,
  DeleteNatConfigurationThunkAction,
  CreateNatConfigurationThunkAction,
  UpdateNatConfigurationThunkAction,
  SetNatConfigurationLoadingAction,
  NatConfigurationActionTypes,
  NatConfiguration,
  SelectNatConfigurationAction,
  GO_TO_NAT_CONFIGURATION_EDIT,
  UPDATE_NAT_CONFIGURATION,
} from './natConfiguration.types';

export function goToNatConfigurationList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_NAT_CONFIGURATION_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NAT_CONFIGURATION_ROUTE}`);
  };
}

export function goToNatConfigurationCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_NAT_CONFIGURATION_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NAT_CONFIGURATION_ROUTE}/create`);
  };
}

export function goToNatConfigurationView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_NAT_CONFIGURATION_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NAT_CONFIGURATION_ROUTE}/view`);
  };
}
export function goToNatConfigurationEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_NAT_CONFIGURATION_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NAT_CONFIGURATION_ROUTE}/edit`);
  };
}

export function setNatConfigurationLoadingAction(
  isLoading: boolean,
): SetNatConfigurationLoadingAction {
  return {
    type: SET_NAT_CONFIGURATION_LOADING,
    payload: { isLoading },
  };
}

export function getNatConfigurationList(deliveryFunctionId: string): NatConfigurationActionTypes {
  return {
    type: GET_NAT_CONFIGURATION_LIST,
    payload: api.getNatConfigurationList(deliveryFunctionId),
  };
}

export function getNatConfiguration(
  deliveryFunctionId: string, id: string,
): NatConfigurationActionTypes {
  return {
    type: GET_NAT_CONFIGURATION,
    payload: api.getNatConfiguration(deliveryFunctionId, id),
  };
}

export function createNatConfiguration(
  deliveryFunctionId: string,
  body: WithAuditDetails<NatConfiguration>,
  history: History,
): CreateNatConfigurationThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_NAT_CONFIGURATION,
        payload: api.createNatConfiguration(deliveryFunctionId, body),
      });

      dispatch(getNatConfigurationList(deliveryFunctionId));
      dispatch(goToNatConfigurationList(history));
      showSuccessMessage(i18n.t('natConfiguration.create.success', { natConfiguration: body.data?.id }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('natConfiguration.create.failed', { natConfiguration: body.data?.id, error: errorMessage }));
    }
  };
}

export function createNatConfigurationFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<NatConfiguration>,
): CreateNatConfigurationThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setNatConfigurationLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_NAT_CONFIGURATION,
        payload: api.createNatConfiguration(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('natConfiguration.create.success', { natConfiguration: body.data?.id }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('natConfiguration.create.failed', { natConfiguration: body.data?.id, error: errorMessage }));

    } finally {
      dispatch(setNatConfigurationLoadingAction(false));
      dispatch(getNatConfigurationList(deliveryFunctionId));
    }
  };
}

export function updateNatConfiguration(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<NatConfiguration>,
  history: History,
): UpdateNatConfigurationThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_NAT_CONFIGURATION,
        payload: api.updateNatConfiguration(deliveryFunctionId, body, id),
      });

      dispatch(getNatConfigurationList(deliveryFunctionId));
      dispatch(goToNatConfigurationList(history));
      showSuccessMessage(i18n.t('natConfiguration.update.success', { name: body.data?.id }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('natConfiguration.update.failed', { name: body.data?.id, error: errorMessage }));
    }
  };
}

export function deleteNatConfiguration(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
  natConfiguration?: string,
): DeleteNatConfigurationThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedNatConfiguration] = getSelectedNatConfiguration(getState());

    try {
      await dispatch({
        type: DELETE_NAT_CONFIGURATION,
        payload: api.deleteNatConfiguration(deliveryFunctionId, id, body),
      });
      showSuccessMessage(i18n.t('natConfiguration.delete.success', { natConfiguration }), 5000);
      if (history != null) {
        dispatch(goToNatConfigurationList(history));
      }
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('natConfiguration.delete.failed', { natConfiguration, error: errorMessage }));

    } finally {
      dispatch(getNatConfigurationList(deliveryFunctionId));
    }
  };
}

export function selectNatConfiguration(
  natConfiguration: NatConfiguration[],
): SelectNatConfigurationAction {
  return {
    type: SELECT_NAT_CONFIGURATION,
    payload: natConfiguration,
  };
}
